import { t } from "@lingui/core/macro";
import { clsx } from "@regrello/core-utils";
import {
  type RegrelloButtonProps,
  RegrelloCheckbox,
  type RegrelloCheckboxProps,
  RegrelloControlWithLabel,
  RegrelloTooltippedInfoIcon,
  RegrelloTypography,
} from "@regrello/ui-core";
import React, { useId } from "react";

import {
  type RegrelloFormFieldBaseProps,
  RegrelloFormFieldLabelPlacement,
} from "./_internal/RegrelloFormFieldBaseProps";
import { RegrelloFormFieldLayout } from "./_internal/RegrelloFormFieldLayout";

export interface RegrelloFormFieldCheckboxProps
  // (clewis): Checkboxes don't work well with the 'selfContainedForm' option, because they don't
  // really consist of a key/value pair. Can maybe revisit in the future (e.g., if we want to show
  // "Label: True/False").
  //
  // (clewis): Checkboxes also don't work well with the 'labelWidth' option, because they don't use
  // the same left-aligned label as other form fields.
  extends Omit<RegrelloFormFieldBaseProps<boolean>, "selfContainedForm">,
    Pick<RegrelloCheckboxProps, "onBlur" | "ref">,
    Pick<RegrelloButtonProps, "onClick"> {
  /**
   * If the label is multi-line, whether to align the checkbox to the `top` of the label or the
   * `center`.
   *
   * @default "center"
   */
  labelAlign?: "center" | "top";
  onChange: (nextChecked: boolean) => void;
  value: NonNullable<RegrelloCheckboxProps["checked"]>;
}

export const RegrelloFormFieldCheckbox = React.memo<RegrelloFormFieldCheckboxProps>(
  React.forwardRef(function RegrelloFormFieldCheckboxFn(
    {
      className,
      dataTestId,
      error,
      endAdornment,
      helperText,
      isDefaultMarginsOmitted,
      isDeleted,
      isEmphasized: _isEmphasized, // (clewis): Extract from checkboxProps so we don't get HTML invalid-attribute errors.
      label,
      labelAlign = "center" as const,
      labelPlacement,
      labelWidth,
      onChange,
      value,
      ...checkboxProps
    },
    ref,
  ) {
    const id = useId();
    const hasError = error != null;

    return (
      <RegrelloFormFieldLayout
        className={clsx("items-center", className, {
          "mb-5": checkboxProps.variant === "spectrum",
        })}
        dataTestId={dataTestId}
        endAdornment={endAdornment}
        error={error}
        helperText={helperText != null ? <div className="mt--2 ml-7">{helperText}</div> : undefined}
        htmlFor={id}
        infoTooltipIconName={checkboxProps.variant !== "spectrum" ? checkboxProps.infoTooltipIconName : undefined}
        infoTooltipText={checkboxProps.variant !== "spectrum" ? checkboxProps.infoTooltipText : undefined}
        infoTooltipVariant={checkboxProps.variant !== "spectrum" ? checkboxProps.infoTooltipVariant : undefined}
        isDefaultMarginsOmitted={isDefaultMarginsOmitted}
        isDeleted={isDeleted}
        isRequiredAsteriskShown={false} // (hchen): Checkbox is always optional
        label={labelPlacement === RegrelloFormFieldLabelPlacement.START ? label : undefined}
        labelWidth={labelPlacement === RegrelloFormFieldLabelPlacement.START ? labelWidth : undefined}
        variant={checkboxProps.variant}
      >
        {labelPlacement === RegrelloFormFieldLabelPlacement.START ? (
          <RegrelloCheckbox
            {...checkboxProps}
            ref={ref}
            checked={value}
            hasError={hasError}
            id={id}
            onCheckedChange={onChange}
          />
        ) : (
          <RegrelloControlWithLabel
            align={labelAlign}
            control={
              <RegrelloCheckbox
                {...checkboxProps}
                ref={ref}
                checked={value}
                hasError={hasError}
                id={id}
                onCheckedChange={onChange}
              />
            }
            disabled={checkboxProps.disabled}
            label={
              checkboxProps.variant === "spectrum" ? (
                // (hchen): Cannot use <FormLabel> here because it blocks the click event on the
                // field text.
                <div className="flex items-center gap-1">
                  <RegrelloTypography className="rg-textFormLabel">{label}</RegrelloTypography>
                  <RegrelloTooltippedInfoIcon
                    iconName={checkboxProps.infoTooltipIconName}
                    tooltipText={checkboxProps.infoTooltipText}
                    variant={checkboxProps.infoTooltipVariant}
                  />
                  {endAdornment}
                  {isDeleted && (
                    <RegrelloTypography className="flex items-center" muted={true}>
                      {`(${t`Deleted`})`}
                      <RegrelloTooltippedInfoIcon
                        iconName="info"
                        tooltipText={t`This field has been deleted from your workspace. We recommend using a different field instead.`}
                        variant="popover"
                      />
                    </RegrelloTypography>
                  )}
                </div>
              ) : (
                <div className="flex items-start gap-1">
                  <RegrelloTypography className="rg-textFormLabel">{label}</RegrelloTypography>
                  {endAdornment}
                  <RegrelloTooltippedInfoIcon
                    className="mt-0.5"
                    iconName={checkboxProps.infoTooltipIconName}
                    tooltipText={checkboxProps.infoTooltipText}
                    variant={checkboxProps.infoTooltipVariant}
                  />
                </div>
              )
            }
          />
        )}
      </RegrelloFormFieldLayout>
    );
  }),
);
